import { useState } from "react";
import {
  AppBar,
  Box,
  Toolbar,
  IconButton,
  Typography,
  Menu,
  Container,
  Button,
  MenuItem,
  Popper,
  Grow,
  Paper,
  ClickAwayListener,
  MenuList,
} from "@mui/material";
import MenuIcon from "@mui/icons-material/Menu";
import CloseIcon from "@mui/icons-material/Close";
import { Link, useNavigate, useLocation } from "react-router-dom";
import logo from "../logo/Legalcastlegroupnbg.png";

const practiceAreas = [
  { title: "Litigation", path: "/practice-areas/litigation" },
  { title: "Banking Law", path: "/practice-areas/banking-law" },
  { title: "Business Law", path: "/practice-areas/business-law" },
  {
    title: "Intellectual Property",
    path: "/practice-areas/intellectual-property",
  },
];

const navigationItems = [
  { title: "Home", path: "/" },
  { title: "Practice Areas", path: "#", hasSubmenu: true },
  { title: "Our Team", path: "#team" },
  { title: "Contact", path: "/contact" },
];

/* ── Shared Cinzel nav-link styles ── */
const navLinkBase = {
  fontFamily: '"Cinzel", serif',
  fontSize: "10px",
  fontWeight: 500,
  letterSpacing: "0.18em",
  textTransform: "uppercase" as const,
  color: "rgba(176,196,222,0.65)",
  position: "relative" as const,
  px: 1.5,
  py: 0.5,
  minWidth: "auto",
  transition: "color 0.3s",
  "&::after": {
    content: '""',
    position: "absolute",
    bottom: 0,
    left: "50%",
    width: 0,
    height: "1px",
    background: "#B0C4DE",
    transition: "width 0.35s ease, left 0.35s ease",
  },
  "&:hover": {
    color: "#B0C4DE",
    background: "transparent",
    "&::after": { width: "100%", left: 0 },
  },
};

const navLinkActive = {
  ...navLinkBase,
  color: "#B0C4DE",
  fontWeight: 600,
  "&::after": {
    content: '""',
    position: "absolute",
    bottom: 0,
    left: 0,
    width: "100%",
    height: "1px",
    background: "#B0C4DE",
  },
};

const Navbar = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const [anchorElNav, setAnchorElNav] = useState<null | HTMLElement>(null);
  const [openPracticeAreas, setOpenPracticeAreas] = useState(false);
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);

  const handleOpenNavMenu = (event: React.MouseEvent<HTMLElement>) => {
    setAnchorElNav(event.currentTarget);
  };

  const handleCloseNavMenu = () => {
    setAnchorElNav(null);
  };

  const handlePracticeAreasClick = (event: React.MouseEvent<HTMLElement>) => {
    setAnchorEl(event.currentTarget);
    setOpenPracticeAreas((prev) => !prev);
  };

  const handlePracticeAreasClose = () => {
    setOpenPracticeAreas(false);
    setAnchorEl(null);
  };

  const handleNavigation = (path: string) => {
    handleCloseNavMenu();
    if (path === "#") return;
    if (path === "/contact") {
      navigate(path);
      window.scrollTo(0, 0);
    } else if (path === "/") {
      navigate(path);
      window.scrollTo(0, 0);
    } else if (path.startsWith("#")) {
      if (location.pathname !== "/") {
        navigate("/");
        setTimeout(() => {
          const element = document.querySelector(path);
          if (element) {
            element.scrollIntoView({ behavior: "smooth" });
          }
        }, 100);
      } else {
        const element = document.querySelector(path);
        if (element) {
          element.scrollIntoView({ behavior: "smooth" });
        }
      }
    } else {
      navigate(path);
      window.scrollTo(0, 0);
    }
  };

  const isActive = (path: string) => {
    if (path === "#team") {
      return location.pathname === "/" && location.hash === "#team";
    }
    return location.pathname === path;
  };

  return (
    <AppBar
      position="fixed"
      sx={{
        background: "rgba(8,21,36,0.92)",
        backdropFilter: "blur(16px)",
        WebkitBackdropFilter: "blur(16px)",
        borderBottom: "1px solid rgba(176,196,222,0.15)",
        boxShadow: "0 2px 12px rgba(0,0,0,0.2)",
        zIndex: 1200,
      }}
    >
      <Container maxWidth="xl">
        <Toolbar disableGutters sx={{ py: 0.8, justifyContent: "space-between" }}>
          {/* ── Brand ── */}
          <Box sx={{ display: "flex", alignItems: "center", flexShrink: 0 }}>
            <Link to="/" style={{ display: "flex", alignItems: "center" }}>
              <img
                src={logo}
                alt="Legal Castle Group"
                style={{
                  height: "48px",
                  width: "auto",
                  marginRight: "14px",
                }}
              />
            </Link>
            <Box>
              <Typography
                noWrap
                component={Link}
                to="/"
                sx={{
                  fontFamily: '"Playfair Display", serif',
                  fontWeight: 600,
                  color: "#F8FBFF",
                  textDecoration: "none",
                  fontSize: { xs: "1rem", md: "1.2rem" },
                  letterSpacing: "0.02em",
                  lineHeight: 1.2,
                  display: "block",
                }}
              >
                Legal Castle Group
              </Typography>
              <Typography
                sx={{
                  fontFamily: '"Cinzel", serif',
                  fontSize: "8px",
                  letterSpacing: "0.25em",
                  textTransform: "uppercase",
                  color: "rgba(176,196,222,0.5)",
                  mt: 0.2,
                  display: { xs: "none", sm: "block" },
                }}
              >
                Satyasya Durgam
              </Typography>
            </Box>
          </Box>

          {/* ── Mobile Menu Icon ── */}
          <Box sx={{ display: { xs: "flex", md: "none" } }}>
            <IconButton
              size="large"
              aria-label="menu"
              onClick={handleOpenNavMenu}
              sx={{ color: "rgba(176,196,222,0.8)" }}
            >
              {anchorElNav ? <CloseIcon /> : <MenuIcon />}
            </IconButton>
            <Menu
              id="menu-appbar"
              anchorEl={anchorElNav}
              disablePortal
              disableScrollLock
              anchorOrigin={{ vertical: "top", horizontal: "right" }}
              keepMounted
              transformOrigin={{ vertical: "top", horizontal: "right" }}
              open={Boolean(anchorElNav)}
              onClose={handleCloseNavMenu}
              slotProps={{
                paper: {
                  sx: {
                    width: "100vw",
                    maxWidth: "100%",
                    position: "absolute",
                    top: "56px !important",
                    left: "0 !important",
                    right: "0",
                    maxHeight: "calc(100vh - 56px)",
                    overflowY: "auto",
                    m: 0,
                    borderRadius: 0,
                    backgroundColor: "#0D2139",
                    border: "none",
                    borderTop: "1px solid rgba(176,196,222,0.1)",
                    padding: "16px",
                    boxShadow: "0 18px 50px rgba(0,0,0,0.5)",
                  },
                },
              }}
            >
              <Box sx={{ display: "flex", flexDirection: "column", gap: 0.5, px: 1 }}>
                {navigationItems
                  .filter((item) => !item.hasSubmenu && item.title !== "Contact")
                  .map((item) => (
                    <MenuItem
                      key={item.title}
                      onClick={() => handleNavigation(item.path)}
                      sx={{
                        borderRadius: 0,
                        color: "rgba(176,196,222,0.8)",
                        fontFamily: '"Cinzel", serif',
                        fontSize: "11px",
                        letterSpacing: "0.15em",
                        textTransform: "uppercase",
                        borderBottom: "1px solid rgba(176,196,222,0.08)",
                        "&:hover": {
                          color: "#B0C4DE",
                          backgroundColor: "rgba(176,196,222,0.05)",
                        },
                      }}
                    >
                      {item.title}
                    </MenuItem>
                  ))}

                {/* Practice Areas Label */}
                <Typography
                  sx={{
                    fontFamily: '"Cinzel", serif',
                    fontSize: "9px",
                    letterSpacing: "0.22em",
                    textTransform: "uppercase",
                    color: "rgba(176,196,222,0.4)",
                    mt: 1.5,
                    mb: 0.5,
                    px: 2,
                  }}
                >
                  Practice Areas
                </Typography>

                {practiceAreas.map((area) => (
                  <MenuItem
                    key={area.title}
                    onClick={() => {
                      handleNavigation(area.path);
                      handleCloseNavMenu();
                    }}
                    sx={{
                      borderRadius: 0,
                      color: "rgba(176,196,222,0.7)",
                      fontFamily: '"Jost", sans-serif',
                      fontSize: "13px",
                      fontWeight: 300,
                      pl: 3,
                      borderBottom: "1px solid rgba(176,196,222,0.05)",
                      "&:hover": {
                        color: "#B0C4DE",
                        backgroundColor: "rgba(176,196,222,0.05)",
                      },
                    }}
                  >
                    {area.title}
                  </MenuItem>
                ))}
              </Box>

              <Box sx={{ px: 1, mt: 2 }}>
                <Button
                  fullWidth
                  onClick={() => handleNavigation("/contact")}
                  sx={{
                    fontFamily: '"Cinzel", serif',
                    fontSize: "10px",
                    letterSpacing: "0.18em",
                    textTransform: "uppercase",
                    background: "linear-gradient(135deg, #4682B4, #2C5F8A)",
                    color: "#F8FBFF",
                    fontWeight: 600,
                    py: 1.2,
                    borderRadius: 0,
                    "&:hover": {
                      background: "linear-gradient(135deg, #5a94c4, #3a6f9a)",
                    },
                  }}
                >
                  Schedule Consultation
                </Button>
              </Box>
            </Menu>
          </Box>

          {/* ── Desktop Navigation ── */}
          <Box
            sx={{
              display: { xs: "none", md: "flex" },
              alignItems: "center",
              gap: 0.5,
            }}
          >
            {navigationItems.map((item) =>
              item.hasSubmenu ? (
                <Box key={item.title} sx={{ position: "relative" }}>
                  <Button
                    onClick={handlePracticeAreasClick}
                    sx={isActive(item.path) ? navLinkActive : navLinkBase}
                  >
                    {item.title}
                  </Button>
                  <Popper
                    open={openPracticeAreas}
                    anchorEl={anchorEl}
                    placement="bottom-start"
                    transition
                    sx={{ zIndex: 1300 }}
                  >
                    {({ TransitionProps }) => (
                      <Grow {...TransitionProps}>
                        <Paper
                          sx={{
                            mt: 1,
                            minWidth: "220px",
                            backgroundColor: "rgba(8,21,36,0.96)",
                            backdropFilter: "blur(12px)",
                            border: "1px solid rgba(176,196,222,0.12)",
                            borderRadius: 0,
                            boxShadow: "0 16px 40px rgba(0,0,0,0.4)",
                          }}
                        >
                          <ClickAwayListener onClickAway={handlePracticeAreasClose}>
                            <MenuList sx={{ py: 0.5 }}>
                              {practiceAreas.map((area) => (
                                <MenuItem
                                  key={area.title}
                                  onClick={() => {
                                    handleNavigation(area.path);
                                    handlePracticeAreasClose();
                                  }}
                                  sx={{
                                    py: 1.2,
                                    px: 2.5,
                                    fontFamily: '"Jost", sans-serif',
                                    fontSize: "13px",
                                    fontWeight: 300,
                                    color: "rgba(176,196,222,0.7)",
                                    letterSpacing: "0.04em",
                                    borderBottom: "1px solid rgba(176,196,222,0.06)",
                                    "&:hover": {
                                      backgroundColor: "rgba(176,196,222,0.06)",
                                      color: "#B0C4DE",
                                    },
                                  }}
                                >
                                  {area.title}
                                </MenuItem>
                              ))}
                            </MenuList>
                          </ClickAwayListener>
                        </Paper>
                      </Grow>
                    )}
                  </Popper>
                </Box>
              ) : (
                <Button
                  key={item.title}
                  onClick={() => handleNavigation(item.path)}
                  sx={isActive(item.path) ? navLinkActive : navLinkBase}
                >
                  {item.title}
                </Button>
              )
            )}

            {/* CTA Button */}
            <Button
              variant="contained"
              onClick={() => handleNavigation("/contact")}
              sx={{
                ml: 2,
                fontFamily: '"Cinzel", serif',
                fontSize: "10px",
                letterSpacing: "0.18em",
                textTransform: "uppercase",
                fontWeight: 600,
                background: "linear-gradient(135deg, #4682B4, #2C5F8A)",
                color: "#F8FBFF",
                px: 3,
                py: 1,
                borderRadius: 0,
                boxShadow: "0 6px 20px rgba(70,130,180,0.3)",
                transition: "all 0.35s ease",
                "&:hover": {
                  background: "linear-gradient(135deg, #5a94c4, #3a6f9a)",
                  boxShadow: "0 8px 28px rgba(70,130,180,0.45)",
                  transform: "translateY(-1px)",
                },
              }}
            >
              Schedule Consultation
            </Button>
          </Box>
        </Toolbar>
      </Container>
    </AppBar>
  );
};

export default Navbar;
